import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { InputComponent } from '../../shared/input/input.component';
import { AccountService } from '../../model/account/account.service';
import { Account } from '../../model/account/account.model';
import { SkeletonModule } from 'primeng/skeleton';
import { InputDisplayGridComponent } from '../../shared/input-display-grid/input-display-grid.component';
import { InputSelectComponent } from '../../shared/input-select/input-select.component';
import { LocalizationService } from '../../shared/localization.service';

interface DisplayValue {
  label?: string;
  value?: string;
  valueComponent?: string | null;
}

@Component({
  standalone: true,
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DropdownModule,
    ButtonModule,
    InputComponent,
    SkeletonModule,
    InputDisplayGridComponent,
    InputSelectComponent
  ]
})
export class AccountSettingsComponent implements OnInit {
  editMode: boolean = false;
  accountForm: FormGroup;
  account?: Account;

  languages = [
    { label: '🇺🇸 United States - English', value: 'EN_US' },
    { label: '🇷🇺 российский', value: 'RU_RU' },
    { label: '🇸🇦 العربية', value: 'AR_EG' }
  ];

  coordinates = [
    {
      label: $localize`:@@coordinates.latLong:Latitude / Longitude`,
      value: 'LAT_LON'
    },
    {
      label: $localize`:@@coordinates.utm:UTM`,
      value: 'UTM'
    }
  ];

  itemRows = [] as DisplayValue[];

  private readonly gpsUnitMap: Record<string, string> = {
    LAT_LON:'LAT_LON',
    UTM: 'UTM'
  };

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private localizationService: LocalizationService
  ) {

    this.accountForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [
        null,
        [Validators.required, Validators.pattern(/^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/)]
      ],
      callSign: [''],
      unit: [''],
      role: [''],
      selectedLanguage: ['EN_US'],
      selectedCoordinates: ['LAT_LON']
    });
  }

  ngOnInit(): void {
    this.loadAccount();
  }

  private async loadAccount(): Promise<void> {
    try {
      this.account = await this.accountService.fetch();
      this.populateForm(this.account);
    } catch (error) {
      console.error('Error loading account:', error);
    }
  }

  private populateForm(account: Account): void {
    this.accountForm.patchValue({
      firstName: account.firstName,
      lastName: account.lastName,
      callSign: account.callSign,
      phone: account.phone,
      unit: account.unit,
    });

    this.accountForm.patchValue({
      selectedLanguage: account.language || 'EN_US',
      selectedCoordinates: account.gpsUnit  || 'latLong'
    }, { emitEvent: false });


    this.itemRows = [
      {
        label: $localize`:@@accountSettings.fields.coordinates:Coordinates`,
        value: this.coordinates.find(coord => coord.value === account.gpsUnit)?.label || account.gpsUnit
      },
      {
        label: $localize`:@@accountSettings.fields.language:Language`,
        value: this.languages.find(lang => lang.value === account.language)?.label || account.language
      },
      {
        label: $localize`:@@accountSettings.fields.firstName:First Name`,
        value: account.firstName
      },
      {
        label: $localize`:@@accountSettings.fields.lastName:Last Name`,
        value: account.lastName
      },
      {
        label: $localize`:@@accountSettings.fields.email:Email Address`,
        value: account.email
      },
      {
        label: $localize`:@@accountSettings.fields.phone:Phone Number`,
        value: account.phone || $localize`:@@common.notAvailable:Not Available`
      },
      {
        label: $localize`:@@accountSettings.fields.unit:Unit`,
        value: account.unit || $localize`:@@common.notAvailable:Not Available`
      },
      {
        label: $localize`:@@accountSettings.fields.callSign:Call Sign`,
        value: account.callSign || $localize`:@@common.notAvailable:Not Available`
      }
    ];
  }


  editAccount(): void {
    this.editMode = true;
  }

  async saveAccount(): Promise<void> {
    this.accountForm.markAllAsTouched();

    if (!this.accountForm.valid) {
      this.markFormGroupTouched(this.accountForm);
      return;
    }

    const { selectedLanguage, selectedCoordinates } = this.accountForm.value;

    const updatedAccount: Account = {
      ...this.account,
      ...this.accountForm.value,
      language: selectedLanguage,
      gpsUnit: selectedCoordinates
    };

    try {
      this.account = await this.accountService.update(updatedAccount);
      this.editMode = false;
      this.populateForm(this.account);
      const selectedLanguage = this.localizationService.getLocaleFromEnum(this.accountForm.get('selectedLanguage')?.value);
      this.localizationService.saveLocalization(selectedLanguage);
      this.localizationService.redirectToLocale(selectedLanguage, 'account-settings');

    } catch (error) {
      console.error('Error saving account:', error);
    }
  }
  
  cancelEdit(): void {
    this.editMode = false;
    if (this.account) this.populateForm(this.account);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get f() {
    return this.accountForm.controls;
  }
  onCoordinatesChange($event) {
    // Handle coordinates change logic here
  }
}
